import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Scale, X, Save } from 'lucide-react';
import { cn } from '../lib/utils';
import { triggerHaptic } from '../lib/haptic';
import { Language } from '../types';

interface WeightLogModalProps {
  isOpen: boolean;
  onClose: () => void;
  lang: Language;
  theme: string;
  illumination?: boolean;
  baseWeight: number;
  weightInput: string;
  setWeightInput: (val: string) => void;
  weightDate: string;
  setWeightDate: (val: string) => void;
  weightNote: string;
  setWeightNote: (val: string) => void;
  onSave: (weightKg: number, dateStr?: string, note?: string) => void;
}

export const WeightLogModal: React.FC<WeightLogModalProps> = ({
  isOpen,
  onClose,
  lang,
  theme,
  illumination,
  baseWeight,
  weightInput,
  setWeightInput,
  weightDate,
  setWeightDate,
  weightNote,
  setWeightNote,
  onSave
}) => {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        className={cn(
          "fixed inset-0 z-[100] flex items-center justify-center p-4",
          theme === 'light' 
            ? "bg-black/15 backdrop-blur-md" 
            : (illumination ? "bg-black/35 backdrop-blur-[2px]" : "bg-black/60 backdrop-blur-sm")
        )}
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ type: "spring", stiffness: 400, damping: 26 }}
          className={cn(
            "w-full relative text-[var(--text)] transition-all duration-300",
            illumination ? "max-w-sm" : "max-w-md"
          )}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={cn(
            "relative border rounded-[32px] card-shadow overflow-hidden transition-all duration-300",
            theme === 'light' 
              ? "border-zinc-200/80 shadow-2xl shadow-zinc-200/50 bg-white p-6 sm:p-7 space-y-5" 
              : "border-[var(--card-border)] bg-[var(--card-bg)] p-6 sm:p-7 space-y-5"
          )}>
            {/* Modal Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-2xl bg-emerald-500/10 dark:bg-emerald-500/20 text-emerald-500 flex items-center justify-center">
                  <Scale className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-base text-zinc-900 dark:text-zinc-50">
                    {lang === 'ua' ? "Запис маси тіла" : "Log Body Weight"}
                  </h3>
                  <p className="text-xs text-zinc-500 dark:text-zinc-400">
                    {lang === 'ua' ? "Відстежуйте динаміку ваги" : "Track weight trend"}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-1.5 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 rounded-xl hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Weight Quick Adjuster & Display */}
            <div className="bg-zinc-50 dark:bg-zinc-800/50 p-4 rounded-2xl border border-zinc-200/60 dark:border-zinc-700/60 text-center space-y-3">
              <div className="flex items-center justify-center gap-2">
                <button
                  onClick={() => {
                    const curr = parseFloat(weightInput) || 70;
                    setWeightInput(String(Math.max(20, Number((curr - 1).toFixed(1)))));
                    triggerHaptic('LIGHT');
                  }}
                  className="px-2.5 py-1.5 rounded-xl bg-zinc-200/70 dark:bg-zinc-700 text-zinc-700 dark:text-zinc-200 font-bold text-xs hover:bg-zinc-300 dark:hover:bg-zinc-600 active:scale-95 transition-all cursor-pointer"
                >
                  -1.0
                </button>
                <button
                  onClick={() => {
                    const curr = parseFloat(weightInput) || 70;
                    setWeightInput(String(Math.max(20, Number((curr - 0.1).toFixed(1)))));
                    triggerHaptic('LIGHT');
                  }}
                  className="px-2.5 py-1.5 rounded-xl bg-zinc-200/70 dark:bg-zinc-700 text-zinc-700 dark:text-zinc-200 font-bold text-xs hover:bg-zinc-300 dark:hover:bg-zinc-600 active:scale-95 transition-all cursor-pointer"
                >
                  -0.1
                </button>

                <div className="relative mx-1">
                  <input
                    type="number"
                    step="0.1"
                    min="20"
                    max="400"
                    value={weightInput}
                    onChange={(e) => setWeightInput(e.target.value)}
                    onBlur={() => {
                      if (!weightInput.trim()) {
                        setWeightInput(String(baseWeight || 70));
                      }
                    }}
                    className="w-24 text-center text-3xl font-black bg-transparent text-zinc-900 dark:text-zinc-50 focus:outline-none focus:ring-0"
                  />
                  <span className="text-xs font-bold text-zinc-400 block -mt-1">kg</span>
                </div>

                <button
                  onClick={() => {
                    const curr = parseFloat(weightInput) || baseWeight || 70;
                    setWeightInput(String(Math.min(400, Number((curr + 0.1).toFixed(1)))));
                    triggerHaptic('LIGHT');
                  }}
                  className="px-2.5 py-1.5 rounded-xl bg-zinc-200/70 dark:bg-zinc-700 text-zinc-700 dark:text-zinc-200 font-bold text-xs hover:bg-zinc-300 dark:hover:bg-zinc-600 active:scale-95 transition-all cursor-pointer"
                >
                  +0.1
                </button>
                <button
                  onClick={() => {
                    const curr = parseFloat(weightInput) || baseWeight || 70;
                    setWeightInput(String(Math.min(400, Number((curr + 1).toFixed(1)))));
                    triggerHaptic('LIGHT');
                  }}
                  className="px-2.5 py-1.5 rounded-xl bg-zinc-200/70 dark:bg-zinc-700 text-zinc-700 dark:text-zinc-200 font-bold text-xs hover:bg-zinc-300 dark:hover:bg-zinc-600 active:scale-95 transition-all cursor-pointer"
                >
                  +1.0
                </button>
              </div>

              {/* Presets */}
              <div className="flex items-center justify-center gap-1.5 pt-1">
                {[-2, -0.5, 0, 0.5, 2].map((delta) => {
                  const base = baseWeight || 70;
                  const targetW = Number((base + delta).toFixed(1));
                  return (
                    <button
                      key={delta}
                      onClick={() => {
                        setWeightInput(String(targetW));
                        triggerHaptic('LIGHT');
                      }}
                      className={cn(
                        "px-2 py-1 rounded-lg text-[10px] font-bold transition-all cursor-pointer",
                        parseFloat(weightInput) === targetW
                          ? "bg-emerald-500 text-white shadow-sm"
                          : "bg-zinc-200/50 dark:bg-zinc-700/60 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-300 dark:hover:bg-zinc-600"
                      )}
                    >
                      {delta === 0 ? (lang === 'ua' ? "Базова" : "Base") : `${delta > 0 ? '+' : ''}${delta}`}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Date & Note Inputs */}
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-bold text-zinc-500 dark:text-zinc-400 mb-1">
                  {lang === 'ua' ? "Дата зважування" : "Date of Weigh-in"}
                </label>
                <input
                  type="date"
                  value={weightDate}
                  onChange={(e) => setWeightDate(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl bg-zinc-50 dark:bg-zinc-800/80 border border-zinc-200/60 dark:border-zinc-700/60 text-xs font-bold text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-zinc-500 dark:text-zinc-400 mb-1">
                  {lang === 'ua' ? "Примітка (необов'язково)" : "Note (optional)"}
                </label>
                <input
                  type="text"
                  placeholder={lang === 'ua' ? "напр. Зранку натщесерце" : "e.g. Morning fasted"}
                  value={weightNote}
                  onChange={(e) => setWeightNote(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl bg-zinc-50 dark:bg-zinc-800/80 border border-zinc-200/60 dark:border-zinc-700/60 text-xs text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-2">
              <button
                onClick={onClose}
                className="flex-1 py-2.5 rounded-xl border border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 font-bold text-xs hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors cursor-pointer"
              >
                {lang === 'ua' ? "Скасувати" : "Cancel"}
              </button>
              <button
                onClick={() => {
                  const val = parseFloat(weightInput) || baseWeight || 70;
                  onSave(val, weightDate, weightNote);
                }}
                className="flex-1 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 active:scale-95 text-white font-bold text-xs shadow-md shadow-emerald-500/20 transition-all flex items-center justify-center gap-1.5 cursor-pointer"
              >
                <Save className="w-3.5 h-3.5" />
                {lang === 'ua' ? "Зберегти" : "Save Weigh-in"}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
